import { ArrowRight, Play, Globe, Smartphone, Palette, Cloud, Megaphone, Search, ShoppingCart, Plug } from "lucide-react";
import { Link } from "react-router-dom";

const marqueeItems = [
  { text: "Web Development", Icon: Globe },
  { text: "App Development", Icon: Smartphone },
  { text: "UI/UX Design", Icon: Palette },
  { text: "Cloud Services", Icon: Cloud },
  { text: "Digital Marketing", Icon: Megaphone },
  { text: "SEO Optimization", Icon: Search },
  { text: "E-Commerce Solutions", Icon: ShoppingCart },
  { text: "API Integration", Icon: Plug },
  { text: "Web Development", Icon: Globe },
  { text: "App Development", Icon: Smartphone },
  { text: "UI/UX Design", Icon: Palette },
  { text: "Cloud Services", Icon: Cloud },
  { text: "Digital Marketing", Icon: Megaphone },
  { text: "SEO Optimization", Icon: Search },
  { text: "E-Commerce Solutions", Icon: ShoppingCart },
  { text: "API Integration", Icon: Plug },
];

export default function HeroSection() {
  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-black via-slate-950 to-gray-900 pt-20"
    >
      {/* Background blobs */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 blob blob-blue" />
      <div className="absolute bottom-1/4 -right-32 w-80 h-80 blob blob-cyan" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-primary/5 blur-3xl pointer-events-none" />

      {/* Spinning rings — desktop only */}
      <div className="hidden lg:block absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[900px] border border-primary/10 rounded-full pointer-events-none animate-spin-slow" />
      <div className="hidden lg:block absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] border border-accent/8 rounded-full pointer-events-none" style={{ animation: "spin-slow 15s linear infinite reverse" }} />

      <div className="container max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">

          {/* Left — CSS animated, no framer-motion */}
          <div>
            <h1
              className="font-poppins font-extrabold text-3xl md:text-4xl lg:text-4xl xl:text-4xl leading-relaxed text-white mb-6 md:mt-12 mt-12 animate-fade-up"
              style={{ animationDuration: "0.5s", animationFillMode: "both" }}
            >
              We Turn{" "}
              <span className="text-gradient">"What If"</span>
              {" "}Into "Done"
            </h1>

            <p
              className="font-inter text-lg md:text-xl lg:text-base text-white leading-relaxed mb-10 max-w-xl animate-fade-up"
              style={{ animationDuration: "0.5s", animationDelay: "0.1s", animationFillMode: "both" }}
            >
              We deliver cutting-edge digital solutions including web development,
              mobile apps, cloud services, and enterprise software to help businesses
              grow in the digital world.
            </p>

            <div
              className="flex flex-wrap gap-4 animate-fade-up"
              style={{ animationDuration: "0.5s", animationDelay: "0.2s", animationFillMode: "both" }}
            >
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2 px-8 py-4 text-base">
                Get a Quote
                <ArrowRight size={18} />
              </Link>
              <Link to="/projects" className="btn-outline-primary inline-flex items-center gap-2 px-8 py-4 text-base">
                <Play size={18} className="text-primary" />
                View Our Work
              </Link>
            </div>

            <div
              className="flex items-center gap-6 mt-12 animate-fade-up"
              style={{ animationDuration: "0.5s", animationDelay: "0.3s", animationFillMode: "both" }}
            >
              {[
                { num: "5+", label: "Years Exp." },
                { num: "18+", label: "Projects" },
                { num: "15+", label: "Clients" },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-poppins font-bold text-2xl text-gradient">{s.num}</div>
                  <div className="font-inter text-xs text-white">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — illustration */}
          <div
            className="relative flex justify-center items-center animate-fade-up"
            style={{ animationDuration: "0.6s", animationDelay: "0.15s", animationFillMode: "both" }}
          >
            <div className="hidden lg:block absolute w-[90%] aspect-square border border-primary/10 rounded-full pointer-events-none animate-spin-slow" />
            <div className="hidden lg:block absolute w-[70%] aspect-square border border-accent/10 rounded-full pointer-events-none" style={{ animation: "spin-slow 15s linear infinite reverse" }} />
            <div className="absolute inset-0 bg-primary/10 blur-3xl rounded-full scale-75" />

            <img
              src="/images/hero-illustration.webp"
              srcSet="/images/hero-illustration-sm.webp 480w, /images/hero-illustration.webp 960w"
              sizes="(max-width: 768px) 480px, 576px"
              alt="Tech illustration"
              className="relative z-10 w-full max-w-xl animate-float drop-shadow-2xl"
              fetchPriority="high"
              decoding="sync"
              width="576"
              height="576"
            />

            {/* Floating cards — desktop only */}
            <div className="hidden lg:block absolute top-12 -left-6 glass-card px-4 py-3 shadow-lg" style={{ animation: "float 5s ease-in-out infinite" }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary/20 flex items-center justify-center"><span className="text-sm">✅</span></div>
                <div>
                  <div className="font-poppins font-semibold text-xs text-foreground">Project Delivered</div>
                  <div className="font-inter text-xs text-accent">On Time & Budget</div>
                </div>
              </div>
            </div>

            <div className="hidden lg:block absolute bottom-20 -right-4 glass-card px-4 py-3 shadow-lg" style={{ animation: "float 6s ease-in-out infinite 2s" }}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-accent/20 flex items-center justify-center"><span className="text-sm">🚀</span></div>
                <div>
                  <div className="font-poppins font-semibold text-xs text-foreground">18+ Projects</div>
                  <div className="font-inter text-xs text-muted-foreground">Successfully Built</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee — hidden on mobile */}
      <div className="hidden sm:block absolute bottom-0 left-0 right-0 overflow-hidden py-4">
        <div className="flex whitespace-nowrap animate-marquee">
          {marqueeItems.map(({ text, Icon }, i) => (
            <span key={i} className="font-inter text-base font-medium text-white mx-8 flex items-center gap-2.5">
              <Icon size={18} className="text-primary shrink-0" /> {text}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
